import { doodBlockConfig, propertyValue } from './dood-block-config';
import {
  buildOrderedBlockDescriptor,
  type DescriptorFieldOrderItem,
} from './descriptor-field-order';
import type { IBlockDescriptor } from './descriptor';

export const SHOPHEROTEMPLATEBLOCK_LEGACY_FIELD_ORDER = [
  "accent_color",
  { type: 'separator' },
  "redirect_url",
  { type: 'separator' },
  "hero_image",
  "hero_logo",
  "hero_baseline",
  "hero_baseline_color",
  "hero_distribution_button_background_color",
  "hero_distribution_button_color",
  { type: 'groupedFields', handle: 'distribution_modes_cards_options', fields: ["width","height"] },
  { type: 'separator' },
  "hero_contextual_heading",
  "hero_contextual_content",
  "hero_contextual_heading_color",
  { type: 'separator' },
  "display_carousel"
] as const satisfies readonly DescriptorFieldOrderItem[];

const {
  shopHeroTemplateBlockDescriptor: _generatedShopHeroTemplateBlockDescriptor,
  shopHeroTemplateBlockBlocDefinition,
  shopHeroTemplateBlockDashboardLayout,
  shopHeroTemplateBlockConfig,
  shopHeroTemplateBlockResolveConfig,
  shopHeroTemplateBlockProperties,
} = doodBlockConfig(
  {
    name: "Shop - Hero Template",
    componentType: 'ShopHeroTemplateBlockComponent',
    category: 'catalog',
    description: "Shop - Hero Template.",
    keywords: [],
    allowed_in: ["GenericPage"],
    sharedFields: {},
    slots: [
      {
        key: 'cards',
        name: 'pages.blocks.shop-hero-template.cards.label',
        accept: 'explicitOnly',
        allowedTypes: ["HeroCardCarouselBlock"],
        slotItemFormat: 'ids',
      }
    ]
  },

  () => ({
    display_navigation: propertyValue({
      type: 'checkbox',
      value: true,
      options: {
        label: "pages.blocks.shop-hero-template.display-navigation.label",
      }
    }),
    navigation_logo: propertyValue({
      type: 'image',
      value: '',
      options: {
        label: "pages.blocks.shop-hero-template.navigation-logo.label",
        help: "pages.blocks.shop-hero-template.navigation-logo.help",
      }
    }),
    accent_color: propertyValue({
      type: 'color',
      value: {"type":"palette","value":"primary-800"},
      options: {
        label: "pages.blocks.shop-hero-template.accent-color.label",
        help: "pages.blocks.shop-hero-template.accent-color.help",
      }
    }),
    redirect_url: propertyValue({
      type: 'text',
      value: '',
      options: {
        label: "pages.blocks.shop-hero-template.redirect-url.label",
        help: "pages.blocks.shop-hero-template.redirect-url.help",
      }
    }),
    hero_image: propertyValue({
      type: 'image',
      value: '',
      options: {
        label: "pages.blocks.shop-hero-template.hero-image.label",
        help: "pages.blocks.shop-hero-template.hero-image.help",
      }
    }),
    hero_logo: propertyValue({
      type: 'image',
      value: '',
      options: {
        label: "pages.blocks.shop-hero-template.hero-logo.label",
        help: "pages.blocks.shop-hero-template.hero-logo.help",
      }
    }),
    hero_baseline: propertyValue({
      type: 'text',
      value: '',
      options: {
        label: "pages.blocks.shop-hero-template.hero-baseline.label",
        help: "pages.blocks.shop-hero-template.hero-baseline.help",
      }
    }),
    hero_baseline_color: propertyValue({
      type: 'color',
      value: {"type":"palette","value":"neutral-600"},
      options: {
        label: "pages.blocks.shop-hero-template.hero-baseline-color.label",
      }
    }),
    hero_distribution_button_background_color: propertyValue({
      type: 'color',
      value: {"type":"hex","value":"#FFFFFF"},
      options: {
        label: "pages.blocks.shop-hero-template.hero-distribution-button-background-color.label",
      }
    }),
    hero_distribution_button_color: propertyValue({
      type: 'color',
      value: {"type":"palette","value":"neutral-600"},
      options: {
        label: "pages.blocks.shop-hero-template.hero-distribution-button-color.label",
      }
    }),
    distribution_modes_cards_options: {
      width: propertyValue({
        type: 'number',
        value: 96,
        options: {
          label: "pages.blocks.shop-hero-template.distribution-modes-cards-options.width.label",
          help: "pages.blocks.shop-hero-template.distribution-modes-cards-options.width.help",
        }
      }),
      height: propertyValue({
        type: 'number',
        value: 96,
        options: {
          label: "pages.blocks.shop-hero-template.distribution-modes-cards-options.height.label",
          help: "pages.blocks.shop-hero-template.distribution-modes-cards-options.height.help",
        }
      })
    },
    hero_contextual_heading: propertyValue({
      type: 'text',
      value: '',
      options: {
        label: "pages.blocks.shop-hero-template.hero-contextual-heading.label",
      }
    }),
    hero_contextual_content: propertyValue({
      type: 'text',
      value: '',
      options: {
        label: "pages.blocks.shop-hero-template.hero-contextual-content.label",
      }
    }),
    hero_contextual_heading_color: propertyValue({
      type: 'color',
      value: {"type":"palette","value":"neutral-600"},
      options: {
        label: "pages.blocks.shop-hero-template.hero-contextual-heading-color.label",
      }
    }),
    display_carousel: propertyValue({
      type: 'checkbox',
      value: false,
      options: {
        label: "pages.blocks.shop-hero-template.display-carousel.label",
        help: "pages.blocks.shop-hero-template.display-carousel.help",
      }
    })
  }),

  () => ({
    omitFields: ["display_navigation", "navigation_logo"] as const,
    tabs: [
      {
        label: "pages.blocks.shop-hero-template.tab.content.label",
        fieldsets: [
          {
            legend: "pages.blocks.shop-hero-template.fieldset.banner.label",
            fields: ["hero_image"],
          },
          {
            legend: "pages.blocks.shop-hero-template.fieldset.logo.label",
            fields: ["hero_logo", "hero_baseline", "hero_baseline_color"],
          },
          {
            legend: "pages.blocks.shop-hero-template.fieldset.distribution.label",
            fields: [
              "hero_distribution_button_background_color",
              "hero_distribution_button_color",
              "distribution_modes_cards_options.width",
              "distribution_modes_cards_options.height",
            ],
          },
          {
            legend: "pages.blocks.shop-hero-template.fieldset.redirect.label",
            fields: ["redirect_url"],
          },
          {
            legend: "pages.blocks.shop-hero-template.fieldset.contextual.label",
            fields: [
              "hero_contextual_heading",
              "hero_contextual_content",
              "hero_contextual_heading_color",
            ],
          },
          {
            legend: "pages.blocks.shop-hero-template.fieldset.carousel.label",
            fields: ["display_carousel", "accent_color", "cards"],
          },
        ],
      },
    ],
  })
);

export function shopHeroTemplateBlockDescriptor(): IBlockDescriptor;
export function shopHeroTemplateBlockDescriptor(
  fieldOrder: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor;
export function shopHeroTemplateBlockDescriptor(
  fieldOrder?: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor {
  if (fieldOrder === undefined) {
    return _generatedShopHeroTemplateBlockDescriptor;
  }
  return buildOrderedBlockDescriptor(_generatedShopHeroTemplateBlockDescriptor, shopHeroTemplateBlockProperties, fieldOrder);
}

export {
  shopHeroTemplateBlockBlocDefinition,
  shopHeroTemplateBlockDashboardLayout,
  shopHeroTemplateBlockProperties,
  shopHeroTemplateBlockConfig,
  shopHeroTemplateBlockResolveConfig,
};

export type ShopHeroTemplateBlockConfig = typeof shopHeroTemplateBlockConfig;
